import { useMemo, useState } from 'react'
import {
  Box, Breadcrumbs, Typography, Stack, Button, Chip, IconButton, Menu, MenuItem, ListItemIcon, ListItemText, Snackbar, Alert,
} from '@mui/material'
import { Add, MoreVert, Edit, CheckCircle, Cancel, PauseCircle, PlayCircle, LocalShipping, Inventory2 } from '@mui/icons-material'
import { DataGrid, type GridColDef } from '@mui/x-data-grid'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { listOrders, cancelOrder, suspendOrder, reactivateOrder, type OrderListItem, type OrderStatus } from '../../api/comenzi'
import { errorMessage } from '../../api/client'
import OrderFormDialog from './OrderFormDialog'
import ConfirmOrderDialog from './ConfirmOrderDialog'
import OrderDeliveriesDialog from './OrderDeliveriesDialog'
import BomFeasibilityDialog from './BomFeasibilityDialog'

const STATUS_LABEL: Record<OrderStatus, string> = {
  Draft: 'Ciornă', Confirmed: 'Confirmată', InProduction: 'În producție', PartiallyDelivered: 'Parțial livrată',
  FullyDelivered: 'Complet livrată', Invoiced: 'Facturată', Closed: 'Închisă', Cancelled: 'Anulată', Suspended: 'Suspendată',
}
const STATUS_COLOR: Record<OrderStatus, 'default' | 'success' | 'warning' | 'info' | 'error'> = {
  Draft: 'default', Confirmed: 'success', InProduction: 'info', PartiallyDelivered: 'warning',
  FullyDelivered: 'success', Invoiced: 'success', Closed: 'default', Cancelled: 'error', Suspended: 'warning',
}

export default function ComenziPage() {
  const [formTarget, setFormTarget] = useState<OrderListItem | null | 'new'>(null)
  const [confirmTarget, setConfirmTarget] = useState<OrderListItem | null>(null)
  const [deliveriesTarget, setDeliveriesTarget] = useState<OrderListItem | null>(null)
  const [feasibilityTarget, setFeasibilityTarget] = useState<OrderListItem | null>(null)
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; row: OrderListItem } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({ queryKey: ['comercial', 'comenzi'], queryFn: listOrders })
  const closeMenu = () => setMenuAnchor(null)
  const invalidate = () => void qc.invalidateQueries({ queryKey: ['comercial', 'comenzi'] })

  const cancelMutation = useMutation({ mutationFn: cancelOrder, onSuccess: invalidate, onError: (e) => setError(errorMessage(e)) })
  const suspendMutation = useMutation({ mutationFn: suspendOrder, onSuccess: invalidate, onError: (e) => setError(errorMessage(e)) })
  const reactivateMutation = useMutation({ mutationFn: reactivateOrder, onSuccess: invalidate, onError: (e) => setError(errorMessage(e)) })

  const columns = useMemo<GridColDef<OrderListItem>[]>(() => [
    { field: 'orderNumber', headerName: 'Nr.', width: 140, renderCell: (p) => <code>{p.row.orderNumber}</code> },
    { field: 'orderDate', headerName: 'Data', width: 110 },
    { field: 'clientName', headerName: 'Client', flex: 1, minWidth: 180 },
    { field: 'agentUserName', headerName: 'Agent', width: 150 },
    {
      field: 'status', headerName: 'Status', width: 150, sortable: false,
      renderCell: (p) => <Chip size="small" label={STATUS_LABEL[p.row.status]} color={STATUS_COLOR[p.row.status]} variant="outlined" />,
    },
    { field: 'total', headerName: 'Total', width: 110, type: 'number', valueFormatter: (v: number) => v.toFixed(2).replace('.', ',') },
    { field: 'currency', headerName: 'Monedă', width: 90 },
    {
      field: 'actions', headerName: '', width: 56, sortable: false, filterable: false, disableColumnMenu: true,
      renderCell: (p) => (
        <IconButton size="small" onClick={(e) => setMenuAnchor({ el: e.currentTarget, row: p.row })}>
          <MoreVert fontSize="small" />
        </IconButton>
      ),
    },
  ], [])

  return (
    <Stack spacing={2}>
      <Breadcrumbs separator="/" sx={{ fontSize: 12.5 }}>
        <Typography variant="inherit" color="text.secondary">Comercial</Typography>
        <Typography variant="inherit" color="text.primary">Comenzi clienți</Typography>
      </Breadcrumbs>
      <Typography variant="h2">Comenzi clienți</Typography>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" startIcon={<Add />} onClick={() => setFormTarget('new')}>Comandă nouă</Button>
      </Box>
      <Box sx={{ height: 560, bgcolor: 'background.paper', borderRadius: 1.5 }}>
        <DataGrid rows={data ?? []} columns={columns} loading={isLoading} density="comfortable" disableRowSelectionOnClick
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }} pageSizeOptions={[25, 50]} />
      </Box>

      <OrderFormDialog target={formTarget} onClose={() => setFormTarget(null)} />
      <ConfirmOrderDialog order={confirmTarget} onClose={() => setConfirmTarget(null)} />
      <OrderDeliveriesDialog order={deliveriesTarget} onClose={() => setDeliveriesTarget(null)} />
      <BomFeasibilityDialog target={feasibilityTarget} onClose={() => setFeasibilityTarget(null)} />

      <Menu anchorEl={menuAnchor?.el} open={!!menuAnchor} onClose={closeMenu}>
        {menuAnchor && [
          <MenuItem key="edit" disabled={menuAnchor.row.status !== 'Draft'} onClick={() => { setFormTarget(menuAnchor.row); closeMenu() }}>
            <ListItemIcon><Edit fontSize="small" /></ListItemIcon><ListItemText>Editare</ListItemText>
          </MenuItem>,
          <MenuItem key="confirm" disabled={menuAnchor.row.status !== 'Draft'} onClick={() => { setConfirmTarget(menuAnchor.row); closeMenu() }}>
            <ListItemIcon><CheckCircle fontSize="small" /></ListItemIcon><ListItemText>Confirmă</ListItemText>
          </MenuItem>,
          <MenuItem key="deliveries" disabled={!['Confirmed', 'PartiallyDelivered', 'InProduction'].includes(menuAnchor.row.status)}
            onClick={() => { setDeliveriesTarget(menuAnchor.row); closeMenu() }}>
            <ListItemIcon><LocalShipping fontSize="small" /></ListItemIcon><ListItemText>Livrări</ListItemText>
          </MenuItem>,
          <MenuItem key="feasibility" disabled={menuAnchor.row.status !== 'Draft'}
            onClick={() => { setFeasibilityTarget(menuAnchor.row); closeMenu() }}>
            <ListItemIcon><Inventory2 fontSize="small" /></ListItemIcon><ListItemText>Verifică fezabilitatea BOM</ListItemText>
          </MenuItem>,
          <MenuItem key="suspend" disabled={!['Confirmed', 'PartiallyDelivered'].includes(menuAnchor.row.status)}
            onClick={() => { suspendMutation.mutate(menuAnchor.row.id); closeMenu() }}>
            <ListItemIcon><PauseCircle fontSize="small" /></ListItemIcon><ListItemText>Suspendă</ListItemText>
          </MenuItem>,
          <MenuItem key="reactivate" disabled={menuAnchor.row.status !== 'Suspended'}
            onClick={() => { reactivateMutation.mutate(menuAnchor.row.id); closeMenu() }}>
            <ListItemIcon><PlayCircle fontSize="small" /></ListItemIcon><ListItemText>Reactivează</ListItemText>
          </MenuItem>,
          <MenuItem key="cancel" disabled={['Closed', 'Cancelled'].includes(menuAnchor.row.status)}
            onClick={() => { cancelMutation.mutate(menuAnchor.row.id); closeMenu() }}>
            <ListItemIcon><Cancel fontSize="small" /></ListItemIcon><ListItemText>Anulează</ListItemText>
          </MenuItem>,
        ]}
      </Menu>

      <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError(null)}>
        <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
      </Snackbar>
    </Stack>
  )
}
